import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS } from "@/lib/assets";
import { ArrowRight, Award, BookOpen, Play } from "lucide-react";

export default function Academy() {
  const { authAxios } = useAuth();
  const { tr, lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({ total_lessons: 0, completed_lessons: 0, progress_percent: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cRes, pRes] = await Promise.all([
          authAxios.get(`/academy/courses?lang=${lang}`),
          authAxios.get(`/academy/progress`),
        ]);
        setCourses(cRes.data);
        setProgress(pRes.data);
      } catch (e) {}
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [lang]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="pfi-overline">{tr("academy_eyebrow")}</div>
            <h1 className="mt-3 font-serif text-3xl sm:text-5xl">{tr("academy_title")}</h1>
            <p className="mt-4 text-[#a9a9b8] max-w-2xl">{tr("academy_intro")}</p>
            <div className="mt-3 text-xs text-[#6c6c7a] font-mono">{tr("academy_membership_note")}</div>
          </div>
          <div className="md:col-span-4 flex justify-center md:justify-end">
            <div className="rounded-2xl bg-white p-4 pfi-gold-border">
              <img src={ASSETS.iptechsLogo} alt="IPTECHS" data-testid="iptechs-logo" className="h-32 w-32 object-contain" />
            </div>
          </div>
        </div>

        {/* Global progress */}
        <div className="mt-10 pfi-card p-6">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="pfi-overline">{tr("academy_progress")}</div>
              <div className="mt-1 font-serif text-2xl">
                {progress.completed_lessons} / {progress.total_lessons} {tr("academy_lessons")}
              </div>
            </div>
            <div className="text-[#e6c687] font-mono">{progress.progress_percent}%</div>
          </div>
          <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#e6c687] to-[#d4af37]" style={{ width: `${progress.progress_percent}%` }} />
          </div>
        </div>

        {/* Course grid */}
        <div className="mt-10">
          <div className="pfi-overline mb-4">{tr("academy_courses")}</div>
          {loading ? (
            <div className="text-[#a9a9b8]">{tr("loading")}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map((c) => (
                <CourseCard key={c.course_id} c={c} tr={tr} />
              ))}
            </div>
          )}
        </div>

        {/* Certification */}
        <div className="mt-12 pfi-card p-8">
          <div className="pfi-overline inline-flex items-center gap-2"><Award size={12} /> {tr("academy_cert_title")}</div>
          <div className="mt-3 font-serif text-2xl">IPTECHS Certified Perfumer</div>
          <p className="mt-3 text-[#a9a9b8] max-w-3xl">{tr("academy_cert_body")}</p>
          <div className="mt-3 text-xs text-[#6c6c7a] font-mono">{tr("academy_cert_addon")}</div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function CourseCard({ c, tr }) {
  const started = c.completed_lessons > 0;
  return (
    <div className="pfi-card p-6 flex flex-col" data-testid={`course-card-${c.course_id}`}>
      <div className="flex items-center gap-2 pfi-overline">
        <BookOpen size={12} /> {c.level} · {c.duration}
      </div>
      <div className="mt-3 font-serif text-xl text-[#f8f8fc]">{c.title}</div>
      <p className="mt-2 text-sm text-[#a9a9b8] leading-relaxed">{c.subtitle}</p>
      <div className="mt-4">
        <div className="text-xs text-[#6c6c7a] font-mono mb-1.5">
          {c.completed_lessons}/{c.total_lessons} {tr("academy_lessons")} · {c.progress_percent}%
        </div>
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#e6c687] to-[#d4af37]" style={{ width: `${c.progress_percent}%` }} />
        </div>
      </div>
      <Link
        data-testid={`course-open-${c.course_id}`}
        to={`/academy/${c.course_id}`}
        className="mt-6 pfi-btn-gold rounded-full px-5 py-2.5 text-xs inline-flex items-center justify-center gap-2 self-start"
      >
        <Play size={12} />
        {started ? tr("academy_continue") : tr("academy_start")}
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}
